import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClassifierReadView, GroupSummaryView, OrganisationSummaryViewAlt } from '../types/types';

type HomeCtxType = {
  filteredMainCategories: number[];
  filteredSubCategories: number[];
  filteredGroups: number[];
  classifiers: ClassifierReadView[];
  setClassifiers: React.Dispatch<React.SetStateAction<ClassifierReadView[]>>;
  resetCategoryFilters: () => void;
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams, options?: { replace?: boolean }) => void;
  selectedOrganisation: OrganisationSummaryViewAlt | undefined;
  setSelectedOrganisation: React.Dispatch<React.SetStateAction<OrganisationSummaryViewAlt | undefined>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  groups: GroupSummaryView[];
  setGroups: React.Dispatch<React.SetStateAction<GroupSummaryView[]>>;
  simpleIcons: Map<number, string>;
  setSimpleIcons: React.Dispatch<React.SetStateAction<Map<number, string>>>;
};

const initialContext: HomeCtxType = {
  filteredMainCategories: [],
  filteredSubCategories: [],
  filteredGroups: [],
  classifiers: [],
  setClassifiers: () => {},
  resetCategoryFilters: () => {},
  searchParams: new URLSearchParams(),
  setSearchParams: (params: URLSearchParams) => {},
  selectedOrganisation: undefined,
  setSelectedOrganisation: () => {},
  searchQuery: '',
  setSearchQuery: () => {},
  loading: false,
  setLoading: () => {},
  groups: [],
  setGroups: () => {},
  simpleIcons: new Map(),
  setSimpleIcons: () => {}
};

const homeCtx = React.createContext<HomeCtxType>(initialContext);

const HomeContext = (props: { children: any }) => {
  const [filteredMainCategories, setFilteredMainCategories] = useState<number[]>([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState<number[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<number[]>([]);
  const [classifiers, setClassifiers] = useState<ClassifierReadView[]>([]);
  const [selectedOrganisation, setSelectedOrganisation] = useState<OrganisationSummaryViewAlt>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [groups, setGroups] = useState<GroupSummaryView[]>([]);
  const [simpleIcons, setSimpleIcons] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    // Parse separate parameters for different classifier types (new system)
    const categoryIds = searchParams.getAll('cat').map((id) => Number(id));
    const subCategoryIds = searchParams.getAll('sub_cat').map((id) => Number(id));
    const groupIds = searchParams.getAll('group').map((id) => Number(id));

    // Also parse old 'category' parameter for backward compatibility
    const oldCategoryIds = searchParams.getAll('category').map((id) => Number(id));

    // Find main categories that are directly selected (new system)
    const mainCategories = classifiers
      .filter((cat) => cat.type === 'CATEGORY' && categoryIds.includes(cat.id))
      .map((cat) => cat.id);

    // Also include categories from old system
    const legacyMainCategories = classifiers
      .filter((cat) => cat.type === 'CATEGORY' && oldCategoryIds.includes(cat.id))
      .map((cat) => cat.id);

    const allMainCategories = [...new Set([...mainCategories, ...legacyMainCategories])];

    setFilteredMainCategories(allMainCategories);
    setFilteredSubCategories(
      classifiers
        .filter(
          (cat) => cat.type === 'SUB_CATEGORY' && (subCategoryIds.includes(cat.id) || oldCategoryIds.includes(cat.id))
        )
        .map((cat) => cat.id)
    );
    setFilteredGroups(
      classifiers
        .filter((cat) => cat.type === 'GROUP' && (groupIds.includes(cat.id) || oldCategoryIds.includes(cat.id)))
        .map((cat) => cat.id)
    );
  }, [searchParams, classifiers]);

  const resetCategoryFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('cat');
    params.delete('sub_cat');
    params.delete('group');
    params.delete('category'); // Also clear old parameter for backward compatibility
    setSearchParams(params);
    setSelectedOrganisation(undefined);
  }, [searchParams, setSearchParams]);

  return (
    <homeCtx.Provider
      value={{
        filteredMainCategories,
        filteredSubCategories,
        filteredGroups,
        classifiers,
        setClassifiers,
        resetCategoryFilters,
        searchParams,
        setSearchParams,
        selectedOrganisation,
        setSelectedOrganisation,
        searchQuery,
        setSearchQuery,
        loading,
        setLoading,
        groups,
        setGroups,
        simpleIcons,
        setSimpleIcons
      }}
    >
      {props.children}
    </homeCtx.Provider>
  );
};

export default HomeContext;
export const useHomeContext = () => React.useContext(homeCtx);
